// Comprobador de shaders para los pasos del Mod Cofrade.
//
// Algunos shaders (familia Chocapic: BSL, Complementary, SEUS, Sildur's, Kappa…) hacen que el
// techo del palio y las bambalinas se muevan CON RETRASO respecto al paso, porque reprocesan la
// animación de huesos con efectos temporales. No es un fallo del mod (pasa con cualquier versión
// y solo con esos shaders). Esta página lo comprueba:
//   - Lista de shaders ya PROBADOS en juego (compatibles / no compatibles).
//   - Escáner: arrastras el .zip y, si no está en la lista, mira dentro (en el navegador, sin
//     subir nada) buscando los efectos temporales que suelen dar el problema, y da una estimación.
//
// Todo es cliente: el zip se lee con la API nativa DecompressionStream, sin librerías.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, CheckCircle2, XCircle, AlertTriangle, HelpCircle, Upload, FlaskConical,
  ShieldCheck, ShieldX, Loader2, ListChecks,
} from 'lucide-react';
import { DEFAULT_SHADERS, getShaderCompat, norm, type ShaderRow } from '@/lib/shader-compat';

// ─── Lector de ZIP mínimo (directorio central + DecompressionStream) ───────────
type ZipEntry = { name: string; method: number; compSize: number; localOff: number };
type Zip = { entries: ZipEntry[]; inflate: (e: ZipEntry) => Promise<string> };

async function leerZip(buf: ArrayBuffer): Promise<Zip> {
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 66000); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('no-zip');
  const total = dv.getUint16(eocd + 10, true);
  let off = dv.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];
  for (let n = 0; n < total; n++) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const method = dv.getUint16(off + 10, true);
    const compSize = dv.getUint32(off + 20, true);
    const nameLen = dv.getUint16(off + 28, true);
    const extraLen = dv.getUint16(off + 30, true);
    const commLen = dv.getUint16(off + 32, true);
    const localOff = dv.getUint32(off + 42, true);
    const name = new TextDecoder().decode(u8.subarray(off + 46, off + 46 + nameLen));
    entries.push({ name, method, compSize, localOff });
    off += 46 + nameLen + extraLen + commLen;
  }
  const inflate = async (e: ZipEntry): Promise<string> => {
    const lNameLen = dv.getUint16(e.localOff + 26, true);
    const lExtraLen = dv.getUint16(e.localOff + 28, true);
    const start = e.localOff + 30 + lNameLen + lExtraLen;
    const raw = u8.subarray(start, start + e.compSize);
    if (e.method === 0) return new TextDecoder().decode(raw);
    const ds = new DecompressionStream('deflate-raw');
    const out = new Response(new Blob([raw]).stream().pipeThrough(ds));
    return new TextDecoder().decode(await out.arrayBuffer());
  };
  return { entries, inflate };
}

// Heurística para shaders DESCONOCIDOS: busca en el shader de entidades señales de efectos
// temporales, que son los que arrastran la animación del palio.
async function estimar(zip: Zip): Promise<'riesgo' | 'dudoso' | 'limpio' | null> {
  const objetivo = zip.entries.filter((e) =>
    /gbuffers_entities.*\.(vsh|glsl)$/i.test(e.name) || /(jitter|taa|temporal)/i.test(e.name),
  ).slice(0, 12);
  if (!objetivo.length) return null;
  let txt = '';
  for (const e of objetivo) { try { txt += '\n' + await zip.inflate(e); } catch { /* ignore */ } }
  const señales = [
    /TAAJitter/i, /\bTAA\b/, /temporal/i, /reproject/i, /motion.?blur/i,
    /previousModelView|gbufferPrevious/i, /taau/i,
  ];
  const hits = señales.filter((r) => r.test(txt)).length;
  return hits >= 2 ? 'riesgo' : hits === 1 ? 'dudoso' : 'limpio';
}

// ─── Veredicto ────────────────────────────────────────────────────────────────
type Estado = 'ok' | 'bad' | 'warn';
type Verdict = { estado: Estado; tag: string; titulo: string; quien?: string; cuerpo: string; nota?: string };

const VERDICT_STYLES: Record<Estado, { border: string; bg: string; chip: string; icon: JSX.Element }> = {
  ok: {
    border: 'border-emerald-500/40', bg: 'bg-emerald-500/10',
    chip: 'bg-emerald-500 text-white', icon: <CheckCircle2 className="h-4 w-4" />,
  },
  bad: {
    border: 'border-rose-500/40', bg: 'bg-rose-500/10',
    chip: 'bg-rose-500 text-white', icon: <XCircle className="h-4 w-4" />,
  },
  warn: {
    border: 'border-amber-500/40', bg: 'bg-amber-500/10',
    chip: 'bg-amber-500 text-black', icon: <AlertTriangle className="h-4 w-4" />,
  },
};

export default function ComprobarShadersPage() {
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [db, setDb] = useState<ShaderRow[]>(DEFAULT_SHADERS);
  const inputRef = useRef<HTMLInputElement>(null);

  // La lista vive en Firestore (la edita el admin). Hasta que llega, se usa la semilla.
  useEffect(() => { getShaderCompat().then(setDb); }, []);

  const { compatibles, incompatibles } = useMemo(() => ({
    compatibles: db.filter((d) => d.ok),
    incompatibles: db.filter((d) => !d.ok),
  }), [db]);

  const analizar = useCallback(async (file: File) => {
    setBusy(true);
    setVerdict(null);
    const n = norm(file.name);

    // 1) ¿Está en la base de datos comprobada?
    const match = db.find((d) => n.includes(d.key));
    if (match) {
      setVerdict(match.ok
        ? {
            estado: 'ok', tag: 'Compatible',
            titulo: 'Con este shader los pasos se ven bien',
            quien: `${match.name} · ${match.ver}`,
            cuerpo: 'Comprobado en juego: el techo del palio y las bambalinas se mueven a la vez, pegados al paso. Adelante.',
          }
        : {
            estado: 'bad', tag: 'No compatible',
            titulo: 'Con este shader el movimiento se ve mal',
            quien: `${match.name} · ${match.ver}`,
            cuerpo: 'Comprobado en juego: el techo del palio va con retraso respecto a las bambalinas. No es un fallo del mod, es cómo este shader trata la animación. Prueba con Photon o MakeUp UltraFast.',
          });
      setBusy(false);
      return;
    }

    // 2) Desconocido: validar que es un shaderpack y estimar.
    let zip: Zip;
    try {
      zip = await leerZip(await file.arrayBuffer());
    } catch {
      setVerdict({
        estado: 'warn', tag: 'No se pudo leer', titulo: 'Esto no parece un shaderpack', quien: file.name,
        cuerpo: 'Suelta el archivo .zip del shaderpack tal cual lo descargaste (sin descomprimir). Si tu navegador es muy antiguo, prueba con Chrome, Edge o Firefox actualizados.',
      });
      setBusy(false);
      return;
    }
    if (!zip.entries.some((e) => /(^|\/)shaders\//i.test(e.name))) {
      setVerdict({
        estado: 'warn', tag: 'No es un shader', titulo: 'Este .zip no contiene un shaderpack', quien: file.name,
        cuerpo: 'Dentro no hay una carpeta "shaders". Asegúrate de soltar el zip del shaderpack, no otra cosa.',
      });
      setBusy(false);
      return;
    }

    let est: Awaited<ReturnType<typeof estimar>> = 'limpio';
    try { est = (await estimar(zip)) || 'limpio'; } catch { /* ignore */ }
    const nota = 'Estimación, no comprobado. Este shader no está en la lista. La única forma segura es probarlo tú (mira «Cómo probarlo») y avisar a un administrador para añadirlo.';

    if (est === 'riesgo') {
      setVerdict({
        estado: 'warn', tag: 'Probablemente mal', titulo: 'Este shader tiene pinta de dar problemas', quien: file.name,
        cuerpo: 'Por dentro usa efectos temporales sobre las entidades, que son justo los que arrastran la animación del palio. Es muy posible que el techo vaya con retraso.',
        nota,
      });
    } else if (est === 'dudoso') {
      setVerdict({
        estado: 'warn', tag: 'Dudoso', titulo: 'No está claro con este shader', quien: file.name,
        cuerpo: 'Tiene algún efecto que podría afectar al movimiento del palio, pero no es seguro.',
        nota,
      });
    } else {
      setVerdict({
        estado: 'warn', tag: 'Desconocido', titulo: 'No parece dar problemas, pero pruébalo', quien: file.name,
        cuerpo: 'No he encontrado dentro los efectos que suelen arrastrar la animación, así que probablemente vaya bien.',
        nota,
      });
    }
    setBusy(false);
  }, [db]);

  const onPick = (f?: File | null) => { if (f) analizar(f); };

  return (
    <div className="space-y-8 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/40 to-card/20 p-6 md:p-8"
      >
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex items-start gap-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
            <Sparkles className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-headline text-3xl font-bold md:text-4xl">Comprobar shaders</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
              Algunos shaders hacen que el techo del palio y las bambalinas se muevan con retraso, como
              despegándose del paso. Comprueba aquí si el tuyo es de esos.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Escáner */}
      <section>
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
          onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setOver(false); }}
          onDrop={(e) => { e.preventDefault(); setOver(false); onPick(e.dataTransfer.files?.[0]); }}
          className={`grid cursor-pointer place-items-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors md:p-12 ${
            over ? 'border-primary bg-primary/10' : 'border-white/12 bg-card/40 hover:border-primary/50'
          }`}
        >
          <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/15 text-primary">
            {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <Upload className="h-7 w-7" />}
          </div>
          <h2 className="mt-4 font-headline text-xl font-bold">Suelta aquí tu shader</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            el archivo <strong className="text-foreground">.zip</strong> del shaderpack (el que metes en la carpeta shaderpacks)
          </p>
          <span className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">
            Elegir archivo…
          </span>
          <input
            ref={inputRef} type="file" accept=".zip" className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </div>

        {verdict && <VerdictCard v={verdict} />}
        <p className="mt-3 text-center text-xs text-muted-foreground">
          La comprobación se hace en tu navegador; el shader no se sube a ningún sitio.
        </p>
      </section>

      {/* Listas conocidas */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-primary" />
          <h2 className="font-headline text-2xl font-bold">Lo que ya está probado</h2>
        </div>
        <p className="-mt-2 text-sm text-muted-foreground">Resultados comprobados en juego.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ShaderList
            title="Se ven bien" rows={compatibles}
            icon={<ShieldCheck className="h-4 w-4 text-emerald-400" />} tone="emerald"
          />
          <ShaderList
            title="El movimiento se ve mal" rows={incompatibles}
            icon={<ShieldX className="h-4 w-4 text-rose-400" />} tone="rose"
          />
        </div>
      </section>

      {/* Recomendación */}
      <section className="rounded-2xl border border-primary/25 bg-primary/10 p-5">
        <p className="font-bold text-primary">Recomendación</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Para procesiones y directos, usa <strong className="text-foreground">Photon</strong>: es bonito, está
          bien mantenido y los pasos se ven perfectos. <strong className="text-foreground">MakeUp UltraFast</strong> es
          la alternativa si necesitas más FPS.
        </p>
      </section>

      {/* Cómo probar */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-primary" />
          <h2 className="font-headline text-2xl font-bold">Cómo probarlo tú mismo</h2>
        </div>
        <p className="-mt-2 text-sm text-muted-foreground">
          Si tu shader no está en la lista, compruébalo en 10 segundos.
        </p>
        <ol className="space-y-3">
          {[
            <>Pon tu shader y coloca un <strong>paso de palio</strong> (Fátima, por ejemplo). Que esté <strong>parado</strong>.</>,
            <>Móntate y <strong>mécelo a los lados</strong> (izquierda/derecha), sin avanzar.</>,
            <>Mira el <strong>techo del palio</strong>: si va con <strong>retraso</strong> respecto a los varales y las
              bambalinas —como arrastrándose—, ese shader <strong>no vale</strong>. Si todo se mece a la vez y pegado, <strong>va bien</strong>.</>,
          ].map((paso, i) => (
            <li key={i} className="relative rounded-xl border border-white/8 bg-card/40 py-3 pl-14 pr-4 text-sm">
              <span className="absolute left-3 top-2.5 grid h-7 w-7 place-items-center rounded-full bg-primary/20 font-headline font-bold text-primary">
                {i + 1}
              </span>
              {paso}
            </li>
          ))}
        </ol>
      </section>

      {/* Por qué */}
      <section className="rounded-2xl border border-white/8 bg-card/40 p-5">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-primary" />
          <h2 className="font-headline text-lg font-bold">¿Por qué pasa esto?</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          No es un fallo del mod: el mismo paso se ve bien con unos shaders y mal con otros, y pasa con
          cualquier versión del mod. Los pasos mueven el techo, los varales y las bambalinas por
          <strong className="text-foreground"> animación interna</strong> (no moviendo el objeto entero). Algunos
          shaders —sobre todo la familia <strong className="text-foreground">Chocapic</strong> (BSL, Complementary,
          SEUS, Sildur's, Kappa…)— reprocesan esa animación con efectos temporales y arrastran las partes que se
          mueven rápido, como el techo. El mod no puede evitarlo: lo decide cada shader por su cuenta.
        </p>
      </section>
    </div>
  );
}

function VerdictCard({ v }: { v: Verdict }) {
  const s = VERDICT_STYLES[v.estado];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`mt-5 rounded-2xl border p-5 ${s.border} ${s.bg}`}
    >
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${s.chip}`}>
        {s.icon} {v.tag}
      </span>
      <h3 className="mt-3 font-headline text-xl font-bold">{v.titulo}</h3>
      {v.quien && <p className="mt-1 font-mono text-xs text-muted-foreground break-all">{v.quien}</p>}
      <p className="mt-3 text-sm">{v.cuerpo}</p>
      {v.nota && (
        <p className="mt-3 border-t border-white/10 pt-3 text-xs text-muted-foreground">{v.nota}</p>
      )}
    </motion.div>
  );
}

function ShaderList({ title, rows, icon, tone }: {
  title: string; rows: ShaderRow[]; icon: JSX.Element; tone: 'emerald' | 'rose';
}) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold">{icon}{title}</h3>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${tone === 'emerald' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              {r.name}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">{r.ver}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
